import { useEffect, useSyncExternalStore } from 'react';
import { api, errorText } from '../../../api';
import { useLive } from '../../../lib/live';

// Gemeinsame Abfrage für mehrere Bausteine (Zähler in der Kopfzeile, Hinweise, Leisten):
// ein Abruf je Live-Ereignis statt einer pro Baustein. Wird nicht mehr gebraucht (z. B. nach dem Abmelden),
// wird der Stand verworfen, damit der nächste Login nichts vom vorigen sieht.
export function createSharedResource<T>(url: string) {
  let data: T | undefined;
  // Fehler beim letzten Laden (null = zuletzt geklappt). Seiten ohne Daten zeigen ihn mit „Erneut versuchen“.
  let error: string | null = null;
  let users = 0;
  let running = false;
  let again = false;
  let scheduled = false;
  const subs = new Set<() => void>();
  const emit = () => subs.forEach((f) => f());

  async function load() {
    if (running) {
      again = true;
      return;
    }
    running = true;
    try {
      do {
        again = false;
        try {
          const d = await api.get<T>(url);
          if (users > 0) {
            data = d;
            error = null;
            emit();
          }
        } catch (err) {
          // alter Stand bleibt stehen, das nächste Ereignis lädt erneut
          if (users > 0) {
            error = errorText(err);
            emit();
          }
        }
      } while (again);
    } finally {
      running = false;
    }
  }

  // Mehrere Bausteine reagieren auf dasselbe Ereignis: nur einmal laden.
  function reload() {
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
      scheduled = false;
      void load();
    });
  }

  const subscribe = (cb: () => void) => {
    subs.add(cb);
    return () => {
      subs.delete(cb);
    };
  };

  function useData(): T | undefined {
    const snap = useSyncExternalStore(subscribe, () => data);
    useEffect(() => {
      users++;
      if (data === undefined) reload();
      return () => {
        users--;
        if (users === 0) {
          data = undefined;
          error = null;
        }
      };
    }, []);
    useLive(['chat'], () => reload());
    return snap;
  }

  // Nur zusammen mit useData im selben Baustein benutzen (useData sorgt fürs Laden).
  const useError = (): string | null => useSyncExternalStore(subscribe, () => error);

  return { useData, useError, reload };
}

// Welcher Chat gerade offen ist („Mandant|Mitarbeiter“). Für ihn gibt es keine Meldung und keinen Zähler.
export function createOpenThread() {
  let open: string | null = null;
  const subs = new Set<() => void>();
  const subscribe = (cb: () => void) => {
    subs.add(cb);
    return () => {
      subs.delete(cb);
    };
  };
  const set = (v: string | null) => {
    open = v;
    subs.forEach((f) => f());
  };
  return {
    get: () => open,
    useOpen: () => useSyncExternalStore(subscribe, () => open),
    // Vom offenen Chat aufgerufen
    useRegister(key: string | null) {
      useEffect(() => {
        set(key);
        return () => {
          if (open === key) set(null);
        };
      }, [key]);
    },
  };
}

export const threadId = (clientId: string, staffId: string) => `${clientId}|${staffId}`;
